import * as React from 'react';
import { push } from 'connected-react-router';
import moment from 'moment';

import NotificationItem from '@common/types/notifications/notification-item';
import { InviteOwnerPayload } from '@common/types/notifications/payloads/invite-owner-payload';
import { RequestEditPayload } from '@common/types/notifications/payloads/request-edit-payload';
import { IntroductionPayload } from '@common/types/notifications/payloads/introduction-payload';
import { ReleaseDeniedPayload } from '@common/types/notifications/payloads/release-denied-payload';
import { InviteCreditsPayload } from '@common/types/notifications/payloads/invite-credits-payload';
import { InviteOperatorPayload } from '@common/types/notifications/payloads/invite-operator-payload';
import { WalletWithdrawlPayload } from '@common/types/notifications/payloads/wallet-withdrawl-payload';
import { ReleaseAcceptedPayload } from '@common/types/notifications/payloads/release-accepted-payload';
import { ReleaseInReviewPayload } from '@common/types/notifications/payloads/release-in-review-payload';
import { MerchOrderCreatedPayload } from '@common/types/notifications/payloads/merch-order-created-payload';
import { InviteRecordLabelPayload } from '@common/types/notifications/payloads/invite-record-label-payload';
import { InviteCollaboratorPayload } from '@common/types/notifications/payloads/invite-collaborator-payload';
import { InviteOwnershipSummaryPayload } from '@common/types/notifications/payloads/invite-ownership-summary-payload';
import { InviteCollaboratorSummaryPayload } from '@common/types/notifications/payloads/invite-collaborator-summary-payload';
import { AutomaticMerchCreatedPayload } from '@common/types/notifications/payloads/automatic-merch-created-payload';
import { SyncOpportunityPayload } from '@common/types/notifications/payloads/sync-opportunity-payload';

import { TracInvite } from '@services/invites/types/trac-invite';
import NotificationIntroduction from '../notification-introduction';
import { NotificationMerchOrder } from '../notification-merch-order';
import getNotificationText from '../notification-templates/getNotificationText';
import NotificationTextCtrl, {
  NotificationTextCtrlProps,
} from '../notification-text';
import NotificationChangesCtrl, {
  NotificationChangesCtrlProps,
} from '../notification-changes';
import NotificationRequestCtrl, {
  NotificationRequestCtrlProps,
} from '../notification-request';
import NotificationCreditTemplate from '../notification-templates/notification-credit-template';
import NotificationWalletWithdrawlTemplate from '../notification-templates/notification-wallet-withdrawl';
import NotificationLabelInviteTemplate from '../notification-templates/notification-label-invite-template';
import NotificationReleasePublishingTemplate from '../notification-templates/notification-release-publishing-template';
import NotificationCollaboratorInviteTemplate from '../notification-templates/notification-collaborator-invite-template';
import { NotificationAutomaticMerchCreatedTemplate } from '../notification-templates/notification-automatic-merch-created';
import NotificationOwnershipSummaryCtrl, {
  NotificationOwnershipSummaryCtrlProps,
} from '../notification-ownership-summary';
import NotificationCollaboratorSummaryCtrl, {
  NotificationCollaboratorSummaryCtrlProps,
} from '../notification-collaborator-summary';
import { useDispatch } from 'react-redux';
import { SetPopupNotification } from '@webapp/store/notifications/action-creators';
import { OpenMenu } from '@webapp/store/ui/action-creators';

interface NotificationsWrapperCtrlProps {
  className?: string;
  notifications: NotificationItem[];
  onClear: (id: string) => void;
}

const NotificationsWrapperCtrl: React.FC<NotificationsWrapperCtrlProps> = ({
  className,
  notifications,
  onClear,
}) => {
  const dispatch = useDispatch();

  const goToCatalog = () => {
    dispatch(push('/catalog'));
  };

  const showSyncNotification = (payload: SyncOpportunityPayload) => {
    dispatch(new SetPopupNotification(payload));
    dispatch(new OpenMenu('SYNC_NOTIFICATION_MENU'));
  };

  return (
    <div className={className}>
      {notifications
        .sort(
          (a, b) =>
            Number(new Date(b.createdAt)) - Number(new Date(a.createdAt))
        )
        .map((notification, key) => {
          const baseProps:
            | NotificationRequestCtrlProps
            | NotificationChangesCtrlProps
            | NotificationTextCtrlProps = {
            isRead: notification.status === 'read',
            imgSrc:
              (notification.payload as ReleaseInReviewPayload).avatar || '',
            timestamp: moment(notification.createdAt).fromNow(),
            children: undefined,
            id: notification.id,
            clearNotification: (id) => onClear(id),
          };

          if (notification.templateName === 'introduction') {
            const payload: IntroductionPayload =
              notification.payload as IntroductionPayload;

            return (
              <NotificationIntroduction
                key={key}
                username={payload.userName}
                {...baseProps}
                clearNotification={(id) => onClear(id)}
              />
            );
          }

          if (notification.templateName === 'request-edit') {
            const notificationProps: NotificationChangesCtrlProps = {
              ...baseProps,
              releaseName: (notification.payload as RequestEditPayload)
                .releaseName,
              changes: (notification.payload as RequestEditPayload).reasons,
              id: notification.id,
              clearNotification: (id) => onClear(id),
            };

            return <NotificationChangesCtrl key={key} {...notificationProps} />;
          }

          if (notification.templateName === 'invite-ownership-summary') {
            const notificationProps: NotificationOwnershipSummaryCtrlProps = {
              ...baseProps,
              userName: (notification.payload as InviteOwnershipSummaryPayload)
                .userName,
              avatar: (notification.payload as InviteOwnershipSummaryPayload)
                .avatar,
              releaseName: (
                notification.payload as InviteOwnershipSummaryPayload
              ).releaseName,
              tracks: (notification.payload as InviteOwnershipSummaryPayload)
                .tracks,
              publishing: (
                notification.payload as InviteOwnershipSummaryPayload
              ).publishing,
              id: notification.id,
              clearNotification: (id) => onClear(id),
            };

            return (
              <NotificationOwnershipSummaryCtrl
                key={key}
                {...notificationProps}
                clearNotification={(id) => onClear(id)}
              />
            );
          }

          if (notification.templateName === 'invite-collaborator-summary') {
            const notificationProps: NotificationCollaboratorSummaryCtrlProps =
              {
                ...baseProps,
                userName: (
                  notification.payload as InviteCollaboratorSummaryPayload
                ).userName,
                avatar: (
                  notification.payload as InviteCollaboratorSummaryPayload
                ).avatar,
                releaseName: (
                  notification.payload as InviteCollaboratorSummaryPayload
                ).releaseName,
                tracks: (
                  notification.payload as InviteCollaboratorSummaryPayload
                ).tracks,
                id: notification.id,
                clearNotification: (id) => onClear(id),
              };

            return (
              <NotificationCollaboratorSummaryCtrl
                key={key}
                {...notificationProps}
                clearNotification={(id) => onClear(id)}
              />
            );
          }

          if (notification.templateName === 'invite-collaborator') {
            const payload: InviteCollaboratorPayload =
              notification.payload as InviteCollaboratorPayload;

            return (
              <NotificationRequestCtrl
                key={key}
                {...baseProps}
                invite={notification.invite as TracInvite}
                clearNotification={(id) => onClear(id)}
              >
                <NotificationCollaboratorInviteTemplate
                  username={payload.userName}
                  songname={payload.songName}
                />
              </NotificationRequestCtrl>
            );
          }

          if (
            notification.templateName === 'invite-owner' ||
            notification.templateName === 'invite-songwriter'
          ) {
            const payload: InviteOwnerPayload =
              notification.payload as InviteOwnerPayload;

            return (
              <NotificationRequestCtrl
                key={key}
                {...baseProps}
                invite={notification.invite as TracInvite}
                clearNotification={(id) => onClear(id)}
                notificationClick={() => goToCatalog()}
              >
                <NotificationCreditTemplate
                  username={payload.userName}
                  songname={payload.songName}
                  percentage={payload.split}
                  isMaster={notification.templateName === 'invite-owner'}
                />
              </NotificationRequestCtrl>
            );
          }

          if (
            notification.templateName === 'invite-operator' ||
            notification.templateName === 'invite-record-label'
          ) {
            const payload: InviteOperatorPayload =
              notification.payload as InviteOperatorPayload;

            return (
              <NotificationRequestCtrl
                key={key}
                {...baseProps}
                invite={notification.invite as TracInvite}
                clearNotification={(id) => onClear(id)}
              >
                <NotificationLabelInviteTemplate
                  username={payload.userName}
                  labelname={payload.recordLabelName}
                  isArtist={notification.templateName === 'invite-record-label'}
                />
              </NotificationRequestCtrl>
            );
          }

          if (notification.templateName === 'release-accepted') {
            const payload: ReleaseAcceptedPayload =
              notification.payload as ReleaseAcceptedPayload;

            return (
              <NotificationTextCtrl key={key} {...baseProps}>
                <NotificationReleasePublishingTemplate
                  releaseName={payload.releaseName}
                />
              </NotificationTextCtrl>
            );
          }
          if (notification.templateName === 'merch-order-created') {
            const payload: MerchOrderCreatedPayload =
              notification.payload as MerchOrderCreatedPayload;
            return (
              <NotificationMerchOrder
                items={payload.items}
                amount={payload.amount}
                {...baseProps}
              />
            );
          }
          if (notification.templateName === 'release-accepted-main') {
            const payload: ReleaseAcceptedPayload =
              notification.payload as ReleaseAcceptedPayload;
            if (payload.dsps.length) {
              return (
                <NotificationTextCtrl key={key} {...baseProps}>
                  <NotificationReleasePublishingTemplate
                    releaseName={payload.releaseName}
                    dsp={payload.dsps[0].name}
                  />
                </NotificationTextCtrl>
              );
            }
          }

          if (notification.templateName === 'release-denied') {
            const payload = notification.payload as ReleaseDeniedPayload;
            return (
              <NotificationTextCtrl key={key} {...baseProps}>
                {getNotificationText('release-denied', {
                  releaseName: payload.releaseName,
                  reasons: payload.reasons,
                })}
              </NotificationTextCtrl>
            );
          }

          if (notification.templateName === 'sync-opportunity') {
            const payload = notification.payload as SyncOpportunityPayload;
            return (
              <NotificationTextCtrl
                key={key}
                {...baseProps}
                notificationClick={() => showSyncNotification(payload)}
              >
                {getNotificationText('sync-opportunity', {
                  subject: payload.subject,
                })}
              </NotificationTextCtrl>
            );
          }

          if (notification.templateName === 'release-in-review') {
            const payload = notification.payload as ReleaseInReviewPayload;
            return (
              <NotificationTextCtrl key={key} {...baseProps}>
                {getNotificationText('release-in-review', {
                  releaseName: payload.releaseName,
                })}
              </NotificationTextCtrl>
            );
          }

          if (notification.templateName === 'release-accepted-others') {
            const payload = notification.payload as ReleaseAcceptedPayload;
            return (
              <NotificationTextCtrl key={key} {...baseProps}>
                {getNotificationText('release-accepted-others', {
                  releaseName: payload.releaseName,
                })}
              </NotificationTextCtrl>
            );
          }

          if (notification.templateName === 'invite-team') {
            const payload = notification.payload as InviteRecordLabelPayload;

            return (
              <NotificationRequestCtrl
                key={key}
                {...baseProps}
                invite={notification.invite as TracInvite}
                clearNotification={(id) => onClear(id)}
              >
                {getNotificationText('invite-team', {
                  userName: payload.userName,
                })}
              </NotificationRequestCtrl>
            );
          }

          if (notification.templateName === 'invite-credits') {
            const payload = notification.payload as InviteCreditsPayload;
            return (
              <NotificationTextCtrl key={key} {...baseProps}>
                {getNotificationText('invite-credits', {
                  songName: payload.songName,
                })}
              </NotificationTextCtrl>
            );
          }

          if (notification.templateName === 'automatic-merch-created') {
            const payload =
              notification.payload as AutomaticMerchCreatedPayload;
            return (
              <NotificationTextCtrl
                key={key}
                {...baseProps}
                imgSrc={payload.cover}
              >
                <NotificationAutomaticMerchCreatedTemplate
                  isRead={baseProps.isRead}
                />
              </NotificationTextCtrl>
            );
          }

          if (
            [
              'wallet-withdrawl-failed',
              'wallet-withdrawl-pending',
              'wallet-withdrawl-succeeded',
            ].some((template) => template === notification.templateName)
          ) {
            const payload = notification.payload as WalletWithdrawlPayload;
            return (
              <NotificationTextCtrl key={key} {...baseProps}>
                <NotificationWalletWithdrawlTemplate
                  sourceAmount={payload.sourceAmount}
                  sourceCurrency={payload.sourceCurrency}
                  destAmount={payload.destAmount}
                  destCurrency={payload.destCurrency}
                  templateName={notification.templateName}
                />
              </NotificationTextCtrl>
            );
          }
          return null;
        })}
    </div>
  );
};

export default NotificationsWrapperCtrl;
